import Backbone from 'backbone';
import React from 'react';
import ReactDOM from 'react-dom';
import _ from 'underscore';

import {EXTERNAL_FILE_TYPE} from 'constants/PreviewTypeConstants';
import ExternalFileOptions from 'external-files/ExternalFileOptions';
import ExternalFilePreview from 'external-files/ExternalFilePreview';
import {performSignedFileQuery} from 'external-files/ExternalFileUtils';
import FileStates from 'FileStates';
import navigateToURL from 'lib/navigateToURL';
import * as navigation from 'Navigation';
import {ExternalFile} from 'notebook/util/NoteUtilConstants';
import {renderToDOM} from 'redux-stores/ReduxDOMRender';

import * as NoteLinkedFileDAO from './NoteLinkedFileDAO';

export class NoteLinkedFileTabState extends navigation.TabState {
  constructor({
    noteLinkedFileId,
    fileName,
    previewFile,
    externalFile,
    isPreviewTransformed,
    owningEntityApiId,
  }: {
    noteLinkedFileId: string;
    fileName: string;
    previewFile?: ExternalFile | null;
    externalFile: ExternalFile | null;
    isPreviewTransformed: boolean;
    owningEntityApiId: string;
  }) {
    // eslint-disable-next-line prefer-rest-params
    super(...arguments);

    // If an external file isn't previewable, we should remove `not_previewable` from the array so that it doesn't appear in the preview dropdown options
    if (externalFile?.availablePreviewTypes.includes(EXTERNAL_FILE_TYPE.NOT_PREVIEWABLE)) {
      externalFile.availablePreviewTypes = [];
    }

    const previewTypeUrlMapping: {[key: string]: string} = {};
    const previewTypeIdMapping: {[key: string]: string} = {};
    // If a previewType is present in both previewFile and externalFile, prefer the url of the externalFile
    previewFile?.availablePreviewTypes.forEach((type) => {
      previewTypeUrlMapping[type] = previewFile.url;
      previewTypeIdMapping[type] = previewFile.id;
    });

    externalFile?.availablePreviewTypes.forEach((type) => {
      if (!(type === EXTERNAL_FILE_TYPE.IMAGE && previewTypeUrlMapping[type])) {
        previewTypeUrlMapping[type] = externalFile.url;
        previewTypeIdMapping[type] = externalFile.id;
      }
    });

    const fileExt = fileName.includes('.') ? fileName.split('.').pop() : '';
    const fileExtLower = fileExt ? fileExt.toLowerCase() : '';

    if (fileExtLower === 'tif' || fileExtLower === 'tiff') {
      previewTypeUrlMapping.image = previewFile?.url || externalFile!.url;
      previewTypeIdMapping.image = previewFile?.id || externalFile!.id;
    }
    // set preview type to first available preview type - preferring externalFile over previewFile
    const previewType = externalFile?.availablePreviewTypes[0] || previewFile?.availablePreviewTypes[0] || '';

    this.set({
      title: fileName,
      previewType,
      previewTypeUrlMapping,
      previewTypeIdMapping,
      noteLinkedFileId,
      owningEntityApiId,
      isPreviewTransformed,
    });
  }

  previewTypeUrlMapping(): Partial<Record<EXTERNAL_FILE_TYPE, string>> {
    return this.get('previewTypeUrlMapping');
  }

  previewTypeIdMapping(): Partial<Record<EXTERNAL_FILE_TYPE, string>> {
    return this.get('previewTypeIdMapping');
  }

  previewType(): EXTERNAL_FILE_TYPE {
    return this.get('previewType');
  }

  isPreviewTransformed(): boolean {
    return this.get('isPreviewTransformed');
  }

  override defaults(): navigation.TabStateDefaults {
    // eslint-disable-next-line no-restricted-syntax
    return _.extend(super.defaults(), {
      title: 'Preview File',
      closeable: true,
      previewUrlMapping: {},
      previewType: '',
      printOptions: [],
    });
  }

  noteLinkedFileId(): string {
    return this.get('noteLinkedFileId');
  }

  owningEntityApiId(): string {
    return this.get('owningEntityApiId');
  }

  static open(
    fileId: string,
    noteLinkedFileId: string,
    fileName: string,
    previewFile: ExternalFile,
    externalFile: ExternalFile,
    isPreviewTransformed: boolean
  ): void {
    const fileState = FileStates.get().get(fileId);
    const newTab = new NoteLinkedFileTabState({
      owningEntityApiId: fileId,
      noteLinkedFileId,
      fileName,
      previewFile,
      externalFile,
      isPreviewTransformed,
    });
    if (fileState?.buffers()) {
      // If tab buffers exist, load the new tab into the buffers.
      const existingTab = fileState.findTab(
        NoteLinkedFileTabState,
        (tab: NoteLinkedFileTabState) => tab.noteLinkedFileId() === noteLinkedFileId
      );

      if (existingTab != null) {
        existingTab.set({active: true});
        return;
      }

      fileState.buffers().addTab(newTab);
    } else {
      // Otherwise, build and navigate to the preview page URL.
      navigateToURL(
        NoteLinkedFileDAO.getPreviewURL(newTab, fileName, noteLinkedFileId, isPreviewTransformed),
        {replace: false}
      );
    }
  }
}

class NoteLinkedFileTabView extends Backbone.View<NoteLinkedFileTabState> {
  static initClass(): void {
    this.prototype.className = 'external-file-wrapper buffer-tab buffer-tab--no-padding';
  }

  override initialize(): void {
    this.listenTo(this.model, 'change', () => this.render());
  }

  override render(): this {
    const previewType = this.model.previewType();
    const url = previewType ? this.model.previewTypeUrlMapping()[previewType]! : '';
    const id = previewType ? this.model.previewTypeIdMapping()[previewType]! : '';
    const owningEntityApiId = this.model.owningEntityApiId();
    renderToDOM(
      <ExternalFilePreview
        url={url}
        externalFileId={id}
        previewType={previewType}
        owningEntityApiId={owningEntityApiId}
        onClose={() => {
          this.model.close();
        }}
      />,
      this.el
    );
    return this;
  }

  override remove(): this {
    ReactDOM.unmountComponentAtNode(this.el);
    super.remove();
    return this;
  }
}
NoteLinkedFileTabView.initClass();

class NoteLinkedFileOptionsView extends Backbone.View<NoteLinkedFileTabState> {
  override initialize(): void {
    this.listenTo(this.model, 'change', () => this.render());
  }

  updatePreviewType = (newPreviewType: string): void => {
    this.model.set('previewType', newPreviewType);
  };

  override render(): NoteLinkedFileOptionsView {
    renderToDOM(
      <ExternalFileOptions
        availablePreviewTypes={Object.keys(this.model.previewTypeUrlMapping()) as Array<EXTERNAL_FILE_TYPE>}
        previewType={this.model.previewType()}
        updatePreviewType={this.updatePreviewType}
        printOptions={[]}
        generateDownloadUrl={async (isPreview: boolean) =>
          isPreview
            ? await performSignedFileQuery(
                this.model.previewType() ? this.model.previewTypeIdMapping()[this.model.previewType()]! : '',
                this.model.previewType() ? this.model.previewTypeUrlMapping()[this.model.previewType()]! : '',
                this.model.owningEntityApiId()
              )
            : NoteLinkedFileDAO.getDownloadURL({
                uuid: this.model.noteLinkedFileId(),
                name: this.model.get('title'),
              })
        }
        isPreviewTransformed={this.model.isPreviewTransformed()}
        closeTab={() => {
          this.model.close();
        }}
      />,
      this.el
    );
    return this;
  }

  override remove(): Backbone.View<NoteLinkedFileTabState> {
    ReactDOM.unmountComponentAtNode(this.el);
    return super.remove();
  }
}

export {
  NoteLinkedFileTabState as tabState,
  NoteLinkedFileTabView as tabView,
  NoteLinkedFileOptionsView as optionsView,
};
